<template>
    <portal>
        <div class="mb-8 d-flex align-center">
            <v-icon color="primary" size="30">mdi-table</v-icon>
            <h1 class="headline ml-4 font-weight-bold grey--text--darken-2 text-uppercase">Adjust Your Plan</h1>
        </div>

        <v-form ref="form" v-model="valid">
        <v-card class="pa-8">
            <v-card-title v-if="personOne !== null" class="mb-8 d-flex align-end">
                <span class="text-h4 font-weight-medium mr-4">
                    {{ personOne.name }}
                </span>
                <span v-if="personOne !== null">
                    {{ personOne.current_age }} years
                </span>
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col>
                        <v-text-field
                            label="First Name"
                            v-model="personOne.name"
                            :rules="firstNameRules"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="4">
                        <v-menu
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="personOne.birth_date"
                                    label="Date of Birth"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                ref="picker"
                                v-model="personOne.birth_date"
                            ></v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col cols="4">
                        <v-radio-group row v-model="personOne.le_strategy">
                            <v-radio label="Male" value="ssa_male"></v-radio>
                            <v-radio label="Female" value="ssa_female"></v-radio>
                        </v-radio-group>
                    </v-col>
                    <v-col cols="4">
                        <v-switch v-model="isMarried" label="Married"></v-switch>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="4">
                        <v-select
                            :items="employmentStatus"
                            label="Employment Status"
                            v-model="personOneEmploymentStatus"
                        ></v-select>
                    </v-col>
                    <v-col cols="1">
                        <v-text-field
                            label="Retirement Age"
                            v-model="personOne.retirement_age"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row v-if="personOneEmploymentStatus === 0 && personOneEmployment !== undefined">
                    <v-col>
                        <v-subheader class="pl-0 text-body-1">
                            <span>Employment Income Monthly</span>
                            <span class="font-weight-medium ml-4">{{ personOneEmployment.value | money }}</span>
                        </v-subheader>
                        <v-slider
                            v-model="personOneEmployment.value"
                            :max="maxIncome"
                            min="0"
                            step="1000"
                        >
                        </v-slider>
                    </v-col>
                </v-row>
                <v-row v-if="personOneEmploymentStatus === 0">
                    <v-col>
                        <v-subheader class="pl-0 text-body-1">
                            <span>Income Goals for Retirement Monthly</span>
                            <span class="font-weight-medium ml-4">{{ incomeNeeds | money }}</span>
                        </v-subheader>
                        <v-slider
                            v-model="incomeNeeds"
                            :max="maxIncome"
                            min="0"
                            step="1000"
                        >
                        </v-slider>
                    </v-col>
                </v-row>
                <v-row v-if="personOneEmploymentStatus === 1 && personOnePension !== undefined">
                    <v-col>
                        <v-subheader class="pl-0 text-body-1">
                            <span>Retirement Income Monthly</span>
                            <span class="font-weight-medium ml-4">{{ personOnePension.value | money }}</span>
                        </v-subheader>
                        <v-slider
                            v-model="personOnePension.value"
                            :max="maxIncome"
                            min="0"
                            step="1000"
                        >
                        </v-slider>
                    </v-col>
                </v-row>
                <v-row v-if="personOneOtherIncome !== undefined">
                    <v-col>
                        <v-subheader class="pl-0 text-body-1">
                            <span>Other Income</span>
                            <span class="font-weight-medium ml-4">{{ personOneOtherIncome.value | money }}</span>
                        </v-subheader>
                        <v-slider
                            v-model="personOneOtherIncome.value"
                            :max="maxIncome"
                            min="0"
                            step="1000"
                        >
                        </v-slider>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-card v-if="isMarried" class="mt-8 pa-8">
            <v-card-title class="mb-8">
                <span class="text-h4">
                    Your Spouse
                </span>
            </v-card-title>
            <v-card-title class="mb-8 d-flex align-end">
                <span class="text-h4 font-weight-medium mr-4">
                    {{ personTwo.name }}
                </span>
                <span>
                    {{ personTwo.current_age }} years
                </span>
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col>
                        <v-text-field
                            label="Spouse First Name"
                            v-model="personTwo.name"
                            :rules="spouseNameRules"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="4">
                        <v-menu
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="personTwo.birth_date"
                                    label="Date of Birth"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                ref="picker"
                                v-model="personTwo.birth_date"
                            ></v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col cols="4">
                        <v-radio-group row v-model="personTwo.le_strategy">
                            <v-radio label="Male" value="ssa_male"></v-radio>
                            <v-radio label="Female" value="ssa_female"></v-radio>
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="4">
                        <v-select
                            :items="employmentStatus"
                            label="Employment Status"
                            v-model="personTwoEmploymentStatus"
                        ></v-select>
                    </v-col>
                    <v-col cols="1">
                        <v-text-field
                            label="Retirement Age"
                            v-model="personTwo.retirement_age"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row v-if="personTwoEmploymentStatus === 0 && personTwoEmployment !== undefined">
                    <v-col>
                        <v-subheader class="pl-0 text-body-1">
                            <span>Employment Income Monthly</span>
                            <span class="font-weight-medium ml-4">{{ personTwoEmployment.value | money }}</span>
                        </v-subheader>
                        <v-slider
                            v-model="personTwoEmployment.value"
                            :max="maxIncome"
                            min="0"
                            step="1000"
                        >
                        </v-slider>
                    </v-col>
                </v-row>
                <v-row v-if="personTwoEmploymentStatus === 1 && personTwoPension !== undefined">
                    <v-col>
                        <v-subheader class="pl-0 text-body-1">
                            <span>Retirement Income Monthly</span>
                            <span class="font-weight-medium ml-4">{{ personTwoPension.value | money }}</span>
                        </v-subheader>
                        <v-slider
                            v-model="personTwoPension.value"
                            :max="maxIncome"
                            min="0"
                            step="1000"
                        >
                        </v-slider>
                    </v-col>
                </v-row>
                <v-row v-if="personTwoOtherIncome !== undefined">
                    <v-col>
                        <v-subheader class="pl-0 text-body-1">
                            <span>Other Income Monthly</span>
                            <span class="font-weight-medium ml-4">{{ personTwoOtherIncome.value | money }}</span>
                        </v-subheader>
                        <v-slider
                            v-model="personTwoOtherIncome.value"
                            :max="maxIncome"
                            min="0"
                            step="1000"
                        >
                        </v-slider>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-row>
            <v-col class="text-center mt-12">
                <v-btn color="primary" depressed rounded height="48" width="300" @click="update">
                    Save
                </v-btn>
            </v-col>
        </v-row>
        </v-form>
    </portal>
</template>

<script>
    import Portal from "@/components/Portal";
    import {mapGetters} from "vuex";

    export default {
        name: "AdjustYourPlan",
        components: {
            Portal
        },
        computed: {
            ...mapGetters('auth', [
                'user',
            ]),
            ...mapGetters('individuals', [
                'personOne',
                'personTwo',
                'personTwoExists',
            ]),
            ...mapGetters('otherIncomes', [
                'otherIncomes',
                'personOneOtherIncome',
                'personTwoOtherIncome',
            ]),
            ...mapGetters('incomeStreams', [
                'personOnePension',
                'personTwoPension',
                'personOneEmployment',
                'personTwoEmployment',
            ]),
            incomeNeeds: {
                get () {
                    return this.$store.getters["incomeNeeds/incomeNeeds"];
                },
                set (value) {
                    this.$store.commit('incomeNeeds/setIncomeNeeds', value);
                }
            },
            spouseNameRules() {
                return [
                    v => (!!v && this.personTwoExists) || 'Spouse First Name is required',
                ]
            }
        },
        data() {
            return {
                personOneEmploymentStatus: 0,
                personTwoEmploymentStatus: 0,
                isMarried: false,
                valid: true,
                maxIncome: 500000,
                employmentStatus: [
                    {
                        text: 'Employed',
                        value: 0,
                    },
                    {
                        text: 'Retired',
                        value: 1,
                    }
                ],
                firstNameRules: [
                    v => !!v || 'First Name is required',
                ],
            }
        },
        mounted() {
            this.loadIndividuals()
            .then(() => {
                this.$store.dispatch('otherIncomes/loadOtherIncomes')
                    .then(() => {
                        if (this.personOneOtherIncome === undefined) {
                            this.$store.commit('otherIncomes/addOtherIncome', this.personOne);
                        }

                        if (this.personTwoExists && this.personTwoOtherIncome === undefined) {
                            this.$store.commit('otherIncomes/addOtherIncome', this.personTwo);
                        }
                    });

                this.$store.dispatch('incomeStreams/loadIncomeStreams');

                this.$store.dispatch('incomeNeeds/loadIncomeNeeds');
            });
        },
        watch: {
            isMarried(newIsMarried) {
                if (newIsMarried && !this.personTwoExists) {
                    this.$store.commit('individuals/addRemovePersonTwo', newIsMarried);
                }
            }
        },
        methods: {
            update() {
                this.$refs.form.validate();

                if (! this.valid) {
                    return;
                }

                this.saveIndividuals()
                    .then(() => {
                        this.loadIndividualsCallback()
                        this.$store.dispatch('otherIncomes/saveOtherIncomes');
                        this.$store.dispatch('incomeStreams/saveIncomeStreams');
                        this.$store.dispatch('incomeNeeds/saveIncomeNeeds');
                    });
            },
            saveIndividuals() {
                // Delete spouse if it existed but the toggle was switched off
                if (! this.isMarried && this.personTwoExists) {
                    this.$store.dispatch('individuals/deleteIndividual', { individual: this.personTwo, index: 1 });

                    // Delete the spouse entities locally and do not send a DELETE request, since they will be removed by the individual deletion
                    if (this.personTwoOtherIncome !== undefined) {
                        this.$store.commit('otherIncomes/deleteOtherIncome', { otherIncome: this.personTwoOtherIncome, index: 1 });
                    }
                    if (this.personTwoPension !== undefined) {
                        this.$store.commit('incomeStreams/deleteIncomeStream', { incomeStream: this.personTwoPension, index: 1 });
                    }
                    if (this.personTwoEmployment !== undefined) {
                        this.$store.commit('incomeStreams/deleteIncomeStream', { incomeStream: this.personTwoEmployment, index: 1 });
                    }
                }

                this.$store.commit('individuals/addRemovePersonTwo', this.isMarried);

                return this.$store.dispatch('individuals/saveIndividuals');
            },
            loadIndividuals() {
                return this.$store.dispatch('individuals/loadIndividuals')
                    .then(() => this.loadIndividualsCallback());
            },
            loadIndividualsCallback() {
                this.isMarried = this.personTwoExists;

                this.personOneEmploymentStatus = (this.personOne.current_age > this.personOne.retirement_age) ? 1 : 0;
            }
        }
    }
</script>
